import React from "react";

/**
 * IndexTileStrip — today_conditions evidence component (pinned, rank 1).
 *
 * Spec §7: "A horizontal strip. Two large numbers on the left — high
 * temperature and rain chance — then five index tiles."
 *
 * Each tile follows the MetricCardShell visual language (rounded-xl, shadow-sm,
 * border-t-[3px] tone accent, ring-1, subtle tint, watermark icon) but with
 * unique weather-specific icons and larger content since each tile only has
 * a label, rating, and index — three text elements that should stand out.
 *
 * Spec references: §0d (colour system), §7 (today_conditions), §6 rule 5 (rating labels).
 */

// ─── Rating → tone style (spec §0d — condition ratings) ────────────────────────
// Card chrome (border, ring, bg) uses the MetricCardShell tone pattern.
// Rating text uses the exact spec §0d 5-level colors.
const RATING_STYLE = {
  "much better than usual for this park": {
    bg: "bg-white dark:bg-gray-800",
    border: "border-gray-200 dark:border-gray-800",
    borderTop: "border-t-emerald-500",
    ring: "ring-1 ring-emerald-500/20 dark:ring-emerald-500/20",
    text: "text-emerald-600 dark:text-emerald-400",
    barFill: "bg-emerald-500",
    watermark: "text-emerald-500/25 dark:text-emerald-500/15",
  },
  "better than usual for this park": {
    bg: "bg-white dark:bg-gray-800",
    border: "border-gray-200 dark:border-gray-800",
    borderTop: "border-t-green-500",
    ring: "ring-1 ring-green-500/20 dark:ring-green-500/20",
    text: "text-green-600 dark:text-green-400",
    barFill: "bg-green-500",
    watermark: "text-green-500/25 dark:text-green-500/15",
  },
  "typical for this park": {
    bg: "bg-white dark:bg-gray-800",
    border: "border-gray-200 dark:border-gray-800",
    borderTop: "border-t-yellow-500",
    ring: "ring-1 ring-yellow-500/20 dark:ring-yellow-500/20",
    text: "text-yellow-600 dark:text-yellow-400",
    barFill: "bg-yellow-500",
    watermark: "text-yellow-500/25 dark:text-yellow-500/15",
  },
  "worse than usual for this park": {
    bg: "bg-white dark:bg-gray-800",
    border: "border-gray-200 dark:border-gray-800",
    borderTop: "border-t-orange-500",
    ring: "ring-1 ring-orange-500/20 dark:ring-orange-500/20",
    text: "text-orange-600 dark:text-orange-400",
    barFill: "bg-orange-500",
    watermark: "text-orange-500/25 dark:text-orange-500/15",
  },
  "much worse than usual for this park": {
    bg: "bg-white dark:bg-gray-800",
    border: "border-gray-200 dark:border-gray-800",
    borderTop: "border-t-red-500",
    ring: "ring-1 ring-red-500/20 dark:ring-red-500/20",
    text: "text-red-600 dark:text-red-400",
    barFill: "bg-red-500",
    watermark: "text-red-500/25 dark:text-red-500/15",
  },
  "not enough history yet": {
    // Spec §0d: #94A3B8 (slate-400), "muted, no fill" — plain bg, not tinted
    bg: "bg-white dark:bg-gray-800",
    border: "border-gray-200 dark:border-gray-800",
    borderTop: "border-t-slate-400 dark:border-t-slate-600",
    ring: "ring-1 ring-slate-400/20 dark:ring-slate-600/20",
    text: "text-slate-400 dark:text-slate-500",
    barFill: "bg-slate-400 dark:bg-slate-600",
    watermark: "text-slate-400/20 dark:text-slate-500/15",
  },
};

const getRatingStyle = (rating) =>
  RATING_STYLE[rating] || RATING_STYLE["not enough history yet"];

// ─── Unique SVG icon per tile (watermark in top-right) ─────────────────────────
const TILE_ICONS = {
  Comfort: (
    <>
      <path d="M12 2a4 4 0 0 0-4 4v1a3 3 0 0 0-3 3v1a3 3 0 0 0-3 3v2a3 3 0 0 0 3 3v1a3 3 0 0 0 3 3h8a3 3 0 0 0 3-3v-1a3 3 0 0 0 3-3v-2a3 3 0 0 0-3-3V9a3 3 0 0 0-3-3V6a4 4 0 0 0-4-4z" />
    </>
  ),
  "Fire risk": (
    <>
      <path d="M12 2c0 4-4 4-4 8a4 4 0 0 0 8 0c0-2-1-3-2-4 0 2-1 3-2 3 0-3 0-5 0-7z" />
    </>
  ),
  Bugs: (
    <>
      <rect x="8" y="6" width="8" height="14" rx="4" />
      <path d="M8 10H4M8 14H3M8 18H4M16 10h4M16 14h5M16 18h4M12 6V3M10 4l-2-1M14 4l2-1" />
    </>
  ),
  "Site conditions": (
    <>
      <path d="M3 20h18M5 20V8l7-5 7 5v12M9 20v-6h6v6" />
    </>
  ),
  Stargazing: (
    <>
      <polygon points="12 2 14 9 21 9 15 13 17 20 12 16 7 20 9 13 3 9 10 9" />
    </>
  ),
};

// ─── Tile order (spec §7: "Order the tiles: Comfort, Fire, Bugs, Site, Stargazing")
const TILES = [
  { label: "Comfort",         indexKey: "comfort_index",    ratingKey: "comfort_rating" },
  { label: "Fire risk",       indexKey: "fire_index",       ratingKey: "fire_rating" },
  { label: "Bugs",            indexKey: "bug_index",        ratingKey: "bug_rating" },
  { label: "Site conditions", indexKey: "site_index",       ratingKey: "site_rating" },
  { label: "Stargazing",      indexKey: "stargazing_index",  ratingKey: "stargazing_rating" },
];

// ─── Shorten rating for display ─────────────────────────────────────────────────
const shortRating = (rating) => {
  if (!rating) return "";
  if (rating === "not enough history yet") return "Not enough data";
  return rating
    .replace(" for this park", "")
    .replace(/^./, (c) => c.toUpperCase());
};

const IndexTileStrip = ({ panel }) => {
  const f = panel.facts || {};

  return (
    <div className="space-y-4">
      {/* ─── Current conditions banner ───────────────────────────────────── */}
      {/* Tinted strip with icons — bridges the panel title and the index tiles */}
      <div className="flex items-stretch rounded-xl bg-gradient-to-r from-orange-50 via-gray-50 to-blue-50 dark:from-orange-500/10 dark:via-gray-800/50 dark:to-blue-500/10 border border-gray-200 dark:border-gray-800 overflow-hidden">
        {/* Temperature */}
        <div className="flex items-center gap-3 flex-1 px-5 py-4">
          <svg className="w-8 h-8 text-orange-500 dark:text-orange-400 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 14.76V5.5a2.5 2.5 0 0 0-5 0v9.26a4.5 4.5 0 1 0 5 0z" />
          </svg>
          <div className="flex flex-col">
            <span className="text-4xl font-extrabold text-orange-500 dark:text-orange-400 leading-none">
              {f.temp_high_f != null ? `${Math.round(f.temp_high_f)}°` : "—"}
            </span>
            <span className="text-xs text-gray-500 dark:text-gray-400 mt-1.5">High temp</span>
          </div>
        </div>

        {/* Divider */}
        <div className="w-px bg-gray-200 dark:bg-gray-700 my-3" />

        {/* Rain chance */}
        <div className="flex items-center gap-3 flex-1 px-5 py-4">
          <svg className="w-8 h-8 text-blue-500 dark:text-blue-400 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 8a5 5 0 0 0-9.6-1.5A4 4 0 1 0 7 15h11a3 3 0 0 0 0-6z" />
            <path d="M9 18l-1 2M13 18l-1 2M17 18l-1 2" />
          </svg>
          <div className="flex flex-col">
            <span className="text-4xl font-extrabold text-blue-500 dark:text-blue-400 leading-none">
              {f.precip_probability_pct}%
            </span>
            <span className="text-xs text-gray-500 dark:text-gray-400 mt-1.5">Rain chance</span>
          </div>
        </div>
      </div>

      {/* ─── Five index tiles ───────────────────────────────────────────── */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        {TILES.map((tile) => {
          const rating = f[tile.ratingKey] || "not enough history yet";
          const index = f[tile.indexKey];
          const style = getRatingStyle(rating);

          return (
            <div
              key={tile.label}
              className={`relative ${style.bg} rounded-xl shadow-sm border border-t-[3px] ${style.borderTop} ${style.border} ${style.ring} p-4 flex flex-col transition-shadow overflow-hidden`}
            >
              {/* Watermark icon (unique per tile) */}
              <div className="absolute inset-0 overflow-hidden rounded-xl pointer-events-none">
                <div className={`absolute -top-4 -right-4 ${style.watermark}`}>
                  <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    {TILE_ICONS[tile.label]}
                  </svg>
                </div>
              </div>

              {/* Tile label */}
              <p className="relative z-10 text-[13px] uppercase tracking-wide font-semibold text-gray-500 dark:text-gray-400">
                {tile.label}
              </p>

              {/* Raw index — the number, large and tone-colored */}
              {index != null && (
                <div className="relative z-10 mt-2 flex items-baseline gap-1">
                  <span className={`text-3xl font-extrabold leading-none ${style.text}`}>
                    {index}
                  </span>
                  <span className="text-xl font-bold text-gray-400 dark:text-gray-500 leading-none">
                    /100
                  </span>
                </div>
              )}

              {/* Progress bar — index out of 100 */}
              {index != null && (
                <div className="relative z-10 w-full h-1.5 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden mt-3">
                  <div
                    className={`h-full ${style.barFill}`}
                    style={{ width: `${Math.min(100, Math.max(0, index))}%` }}
                  />
                </div>
              )}

              {/* Rating — the finding, tone-colored label */}
              <p className={`relative z-10 text-xs font-semibold mt-3 ${style.text}`}>
                {shortRating(rating)}
              </p>
            </div>
          );
        })}
      </div>

      {/* ─── Caption ────────────────────────────────────────────────────── */}
      {f.days_compared && (
        <p className="text-xs text-gray-400 dark:text-gray-500">
          Compared with the last {f.days_compared} days at this property.
        </p>
      )}
    </div>
  );
};

export default IndexTileStrip;